import { useEffect, useLayoutEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { savePrefs } from '../../auth/passwordSession';
import { useSession } from '../../auth/useSession';
import { toast } from '../../community/ui/toast';

/**
 * The image viewer's theme (decided 2026-09-10): dark by default, light when
 * the person chooses it, in Settings or at the bottom of the viewer. Only the
 * viewer follows it; the rest of the community shell is light by design. The
 * enterprise viewer follows it too (2026-10-04: the viewer is the same page
 * in both editions), and its shell, dark by default, goes light around it.
 *
 * Signed in, the choice is an account preference (`viewerLight`), so it
 * follows the person between devices. The last choice is also kept in this
 * browser, so the viewer opens in the right theme before the session has
 * answered, and so a guest's choice sticks.
 */
const KEY = 'tr.viewerLight';

function stored(): boolean {
  try {
    return window.localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

const useLocal = create<{ light: boolean; set: (light: boolean) => void }>((set) => ({
  light: stored(),
  set: (light) => {
    try {
      window.localStorage.setItem(KEY, light ? '1' : '0');
    } catch {
      /* private browsing: the choice lasts this page */
    }
    set({ light });
  },
}));

type SessionLike = { userId?: string | null; prefs?: Record<string, unknown> } | null | undefined;

/** [light, setLight]: the account's choice when signed in, else this browser's. */
export function useViewerLight(): [boolean, (light: boolean) => void] {
  const { data: me } = useSession();
  const qc = useQueryClient();
  const local = useLocal((s) => s.light);
  const setLocal = useLocal((s) => s.set);
  const pref = me?.prefs?.viewerLight;
  const light = typeof pref === 'boolean' ? pref : local;
  // Keep this browser in step with the account, so the next viewer opens in
  // the account's theme at once instead of flashing the other one.
  useEffect(() => {
    if (typeof pref === 'boolean' && pref !== local) setLocal(pref);
  }, [pref, local, setLocal]);
  const set = (v: boolean) => {
    setLocal(v);
    if (!me?.userId) return;
    qc.setQueryData<SessionLike>(['session'], (old) => (old ? { ...old, prefs: { ...(old.prefs ?? {}), viewerLight: v } } : old));
    savePrefs({ viewerLight: v }).catch(() => {
      toast('Your theme could not be saved');
      void qc.invalidateQueries({ queryKey: ['session'] });
    });
  };
  return [light, set];
}

/**
 * While the viewer is on screen the document carries its theme: the `dark`
 * class drives both the viewer's own palette (index.css) and the community
 * tokens (community.css). Whatever the document had before is put back on
 * the way out, so leaving the viewer returns the shell to light (community)
 * or dark (enterprise).
 */
export function useApplyViewerTheme(light: boolean): void {
  const before = useRef<boolean | null>(null);
  useLayoutEffect(() => {
    const html = document.documentElement;
    if (before.current === null) before.current = html.classList.contains('dark');
    html.classList.toggle('dark', !light);
  }, [light]);
  useLayoutEffect(
    () => () => {
      document.documentElement.classList.toggle('dark', !!before.current);
    },
    [],
  );
}
